'use client';

import { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import { addDays, differenceInYears } from 'date-fns';
import { useRouter } from 'next/navigation';
import pluralize from 'pluralize';

import { Button, DatePicker, Icon, Input, Link } from '@/components';
import { useEcomStoreSelector, useForm } from '@/data';
import { AgeGateDocument } from '@/types';

import styles from './age.module.scss';
import {
  IAgeTermsData,
  IProcessedAgeGateData,
} from 'app/_components/AgeGate/utils';
import { CookiesNames } from 'constants/cookies';
import { useCookies } from '@/hooks';

export interface AgeGateData {
  ageGate: AgeGateDocument;
  crmModal: AgeGateDocument;
  header: AgeGateDocument;
}
interface AgeGateProps {
  data: IProcessedAgeGateData | null;
  redirectUrl?: string;
}

const AgeGate: FC<AgeGateProps> = ({ data, redirectUrl }) => {
  const router = useRouter();
  const { setCookieObject } = useCookies();
  const [isLoading, setIsLoading] = useState(false);
  const { is_subscribed_to_crm, setState } = useEcomStoreSelector([
    'is_subscribed_to_crm',
  ]);

  const dataAgeGate = data?.ageGate?.data;
  const crmModal = data?.crmModal?.data;

  // Use data directly from server without unnecessary memoization
  const type = dataAgeGate?.type || '';
  const buttonType = dataAgeGate?.button_type;
  const agreements = dataAgeGate?.agreements || [];
  const rememberMeDays = dataAgeGate?.remember_me;
  const allowedAge = dataAgeGate?.allowed_age || 0;
  const delay =
    parseInt(process?.env?.NEXT_PUBLIC_DELAY_CRMMODAL ?? '100') || 100;
  // Only memoize calculated text that depends on data that can change
  const rememberText = useMemo(() => {
    const daysLabel = `${pluralize('day', rememberMeDays || 0)}`;
    return `Remember me for ${rememberMeDays} ${daysLabel}.`;
  }, [rememberMeDays]);

  // Memoize initial agreements only because they are used in useForm
  const initialAgreements = useMemo(() => {
    const agreementsObj = {
      ...(rememberMeDays ? { remember_me: true } : {}),
    };

    if (agreements.length) {
      agreements.forEach((agreement: any, id) => {
        agreementsObj[`agreement_${id}`] = agreement.default_state;
      });
    }

    return agreementsObj;
  }, [rememberMeDays, agreements]);

  // Form states
  const [birthDate, setBirthDate] = useState<Date>();
  const [hasIncompleteFields, setHasIncompleteFields] = useState(false);
  const [isYounger, setIsYounger] = useState(false);
  const [agreementsAccepted, setAgreementsAccepted] = useState(true);

  const { inputs, genericChange } = useForm(initialAgreements);

  // Memoize error message
  const errorMessage = useMemo(() => {
    if (hasIncompleteFields) {
      return 'Please enter a valid date of birth.';
    }
    if (isYounger) {
      return `You must be at least ${allowedAge} years old.`;
    }
    if (!agreementsAccepted) {
      return 'You must agree to our terms before continuing.';
    }
    return null;
  }, [hasIncompleteFields, isYounger, agreementsAccepted, allowedAge]);

  // Callback to clear errors
  const clearErrorMessage = useCallback(() => {
    setTimeout(() => {
      setHasIncompleteFields(false);
      setIsYounger(false);
      setAgreementsAccepted(true);
    }, 4000);
  }, []);

  // Callback to verify agreements
  const areAgreementsAccepted = useCallback(() => {
    if (!inputs) return true;

    const accepted = Object.values(inputs).every(v => v);
    setAgreementsAccepted(accepted);

    if (!accepted) {
      clearErrorMessage();
      return false;
    }

    return true;
  }, [inputs, clearErrorMessage]);

  // Callback to handle submission
  const handleSubmit = useCallback(
    async (event: Event) => {
      event.preventDefault();

      if (!areAgreementsAccepted()) return;

      setIsLoading(true);

      // Calculate expiration date for local state (optional)
      const days =
        inputs.remember_me && rememberMeDays ? rememberMeDays : undefined; // endpoint will handle fallback
      const expDate = days ? addDays(new Date(), days) : null;

      // Local state for your app (don't confuse with server cookie)
      setState({
        terms_accepted: true,
        terms_accepted_exp_date: expDate,
      });

      // Set cookie
      const cookieData: IAgeTermsData = {
        terms_accepted: true,
        terms_accepted_exp_date: expDate?.toISOString() || null,
      };

      const expDays = expDate
        ? Math.ceil((expDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        : 30; // Default 30 days

      setCookieObject(CookiesNames._TERMS_ACCEPTED, cookieData, {
        maxAge: expDays > 0 ? expDays * 24 * 60 * 60 : 30 * 24 * 60 * 60,
      });

      // SHOW CRM MODAL BEFORE REDIRECTING
      if (crmModal && is_subscribed_to_crm === null) {
        const modal = document.getElementById('age-gate-modal');
        if (modal) {
          modal.style.display = 'none';
        }
        setIsLoading(false);
        setState({ is_crm_modal_open: true });
      }

      // Get return URL with fallback to home
      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get('returnUrl');

      // Use redirectUrl prop if available, otherwise use returnUrl from query params
      const targetUrl = redirectUrl || returnUrl;

      // Decode the URL and validate that it is secure
      const decodedReturnUrl = targetUrl ? decodeURIComponent(targetUrl) : '/';

      //Validate that the URL is relative (security)
      const isValidUrl =
        decodedReturnUrl.startsWith('/') && !decodedReturnUrl.startsWith('//');
      const finalReturnUrl = isValidUrl ? decodedReturnUrl : '/';

      // Redirect
      router.replace(finalReturnUrl);
    },
    [
      areAgreementsAccepted,
      inputs.remember_me,
      rememberMeDays,
      is_subscribed_to_crm,
      delay,
      setState,
      router,
    ],
  );

  // Callback to handle submission with date
  const handleSubmitDate = useCallback(
    (event: any) => {
      event.preventDefault();

      if (!birthDate) {
        setHasIncompleteFields(true);
        clearErrorMessage();
        return;
      }

      if (differenceInYears(new Date(), birthDate) < allowedAge) {
        setIsYounger(true);
        clearErrorMessage();
        return;
      }

      handleSubmit(event);
    },
    [birthDate, allowedAge, clearErrorMessage, handleSubmit],
  );

  // Callbacks for form changes
  const handleAgreementChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      genericChange(event.target.name, event.target.checked);
    },
    [genericChange],
  );

  const handleRememberMeCheck = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      genericChange(event.target.name, event.target.checked);
    },
    [genericChange],
  );

  return (
    <>
      {type === 'Date of Birth' && <DatePicker onChange={setBirthDate} />}
      {!!rememberMeDays && (
        <Input
          checked={inputs.remember_me}
          className={styles.option}
          data-testid="remember-me-checkbox"
          inputLabel={rememberText}
          name="remember_me"
          onChange={handleRememberMeCheck}
          type="checkbox"
        />
      )}
      {agreements && (
        <form>
          {agreements.map(
            (cl, id) =>
              cl?.text && (
                <Input
                  checked={inputs[`agreement_${id}`]}
                  className={styles.option}
                  inputLabel={cl.text}
                  key={`agreement_${id}`}
                  name={`agreement_${id}`}
                  onChange={handleAgreementChange}
                  type="checkbox"
                />
              ),
          )}
        </form>
      )}
      {!!rememberMeDays && agreements && <hr className={styles.separator} />}
      {errorMessage && (
        <p
          className={styles.age_gate__error}
          data-testid="age-gate-error-message"
        >
          <Icon name="info" />
          {errorMessage}
        </p>
      )}
      <div className={styles.age_gate__actions}>
        <Button
          color={buttonType ?? 'primary'}
          data-testid="age-gate-submit-button"
          loading={isLoading}
          onClick={type === 'Date of Birth' ? handleSubmitDate : handleSubmit}
        >
          {data?.ageGate?.data.button_text}
        </Button>
        <Link
          className="button button_sm button_color-link age__exit"
          href="about:blank"
        >
          Exit the site
        </Link>
      </div>
    </>
  );
};

export { AgeGate };
